import { useAppDispatch } from "@/core/app/hooks"
import { setIsRouteChangeBlocked } from "@/core/app/slices/navigationPopup/navigationPopupSlice"
import {
  CaseTagType,
  IncludeLabelType,
  SelectPositionType,
  YesNoType,
} from "@/core/app/slices/user/user.type"
import { Box, MenuItem, Select, Typography } from "@mui/material"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormLabel from "@mui/material/FormLabel"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { 
  SAMPLE_DATA, 
  STATIC_SAMPLE_DATA, 
  LABEL_COMPOSER_OPTIONS,
  type DropdownOption 
} from "../constants"

interface CustomDropdownProps {
  value: string
  handleChange: (value: string) => void
  options: DropdownOption[]
  disabled?: boolean
}

const CustomDropdown = ({
  value,
  handleChange,
  options,
  disabled = false,
}: CustomDropdownProps) => {
  return (
    <Select
      disabled={disabled}
      value={value}
      onChange={(event) => {
        handleChange(event.target.value as string)
      }}
      displayEmpty
      inputProps={{ "aria-label": "Without label" }}
      sx={{ width: "100%", height: "40px", marginLeft: "8px" }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  )
}

export const Label = ({
  currPrint,
  updateModel,
}) => {
  const { t } = useTranslation("common")
  const dispatch = useAppDispatch()
  // select position
  const [toothPosition, setToothPosition] = useState("") // Bottom /WallLingual/ Wall
  // checkbox if No label
  const [checkNoLabel, setCheckNoLabel] = useState("")
  const [caseTag, setCaseTag] = useState([])
  const [updateCaseTagAPI, setUpdateCaseTagAPI] = useState(false)

  // Auto-adjust GenerateShell based on SelectPositionLabel value
  const updateShellBasedOnPosition = (positionValue: SelectPositionType, currentShell: YesNoType) => {
    // Don't manipulate if value is LingualWall
    if (positionValue === SelectPositionType.LingualWall) {
      return
    }
    
    const shellMap = {
      [SelectPositionType.Bottom]: YesNoType.No,
      [SelectPositionType.BuccalWall]: YesNoType.Yes
    }
    
    const requiredShell = shellMap[positionValue]
    if (requiredShell !== undefined && currentShell !== requiredShell) {
      dispatch(updateModel({ GenerateShell: requiredShell }))
    }
  }
  
  // Use imported constants
  const sampleData = SAMPLE_DATA
  const staticSampleData = STATIC_SAMPLE_DATA
  
  // Example output state and validation
  const [exampleOutput, setExampleOutput] = useState("01JamAnderso")
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  // Validate example output
  const validateExampleOutput = (value: string) => {
    const errors: string[] = []
    
    // Check if Stage Number is selected in any dropdown
    const hasStageNumber = caseTag?.some(tagValue => tagValue === CaseTagType.StageNumber)
    
    if (!hasStageNumber) {
      errors.push("Stage Number is required")
    }
    
    setValidationErrors(errors)
    return errors
  }
  
  // Re-validate when caseTag changes (for manual edits to example output)
  useEffect(() => {
    validateExampleOutput(exampleOutput)
  }, [exampleOutput])
  
  // Block navigation when there are validation errors
  useEffect(() => {
    if (validationErrors.length > 0) {
    dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: true }))
    }else{
      dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: false }))

    }
  }, [validationErrors])
  
  // Generate example output based on selected caseTag values
  const generateExampleOutputFromCaseTag = () => {
    let output = ""
    
    caseTag?.forEach((tagValue) => {
      const option = LABEL_COMPOSER_OPTIONS.find(opt => opt.value === tagValue)
      if (option && option.label !== "Empty" && sampleData[option.label]) {
        output += sampleData[option.label]
      }
    })
    
    return output
  }
  


  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as IncludeLabelType
    setCheckNoLabel(value)
    dispatch(updateModel({ IncludeLabelModel: value }))
  }

  const handleToothPosition = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as SelectPositionType
    setToothPosition(value)
    dispatch(updateModel({ SelectPositionLabel: value }))
    updateShellBasedOnPosition(value, currPrint?.GenerateShell)
  }

  // Handle dropdown value changes
  const handleDropdownChange = (index: number, value: string) => {
    const newCaseTag = [...caseTag]
    const numValue = Number(value)
    newCaseTag[index] = numValue
    setCaseTag(newCaseTag)
    setUpdateCaseTagAPI(true)
  }

  // init value
  useEffect(() => {
    setCheckNoLabel(currPrint?.IncludeLabelModel)
    setToothPosition(currPrint?.SelectPositionLabel)
    setCaseTag(
      currPrint?.CaseTag || [
        CaseTagType.StageNumber,
        CaseTagType.FirstNameInitial,
        CaseTagType.LastNameShort,
        CaseTagType.Empty,
      ],
    )
  }, [currPrint?.IncludeLabelModel, currPrint?.SelectPositionLabel, currPrint?.CaseTag])

  useEffect(() => {
    if (caseTag.length > 0) {
      // Update example output based on selected caseTag combinations
      const newExampleOutput = generateExampleOutputFromCaseTag()
      setExampleOutput(newExampleOutput)
      const errors = validateExampleOutput(newExampleOutput)
      if (!errors.length && updateCaseTagAPI) {
        dispatch(updateModel({ CaseTag: caseTag }))
        setUpdateCaseTagAPI(false) // Reset after update
      }
    } else {
      setExampleOutput("")
      validateExampleOutput("")
    }
  }, [caseTag])
  return (
    <Box
      sx={{ width: "100%", display: "flex", flexDirection: "column" }}
      gap={3}
    >
      <FormControl>
        <FormLabel id="labelModel">{`Include label on model`}</FormLabel>
        <RadioGroup
          row
          sx={{ marginLeft: "10px", gap: "40px" }}
          aria-labelledby="labelModel"
          name="radio-buttons-group"
          value={checkNoLabel}
          onChange={handleCheck}
        >
          <FormControlLabel
            value={IncludeLabelType.Yes}
            control={<Radio />}
            label={t(`stlPrinting.optionYes`)}
          />
          <FormControlLabel
            value={IncludeLabelType.No}
            control={<Radio />}
            label={t(`stlPrinting.optionNo`)}
          />
        </RadioGroup>
      </FormControl>
      <Box sx={{ width: "100%", display: "flex" }}>
        <Box sx={{ marginRight: "10px", width: "calc(50% - 5px)" }}>
          <FormControl>
            <FormLabel id="toothPosition">
              {t("stlPrinting.selectPosition")}
            </FormLabel>
            <RadioGroup
              sx={{ marginLeft: "10px" }}
              aria-labelledby="toothPosition"
              name="radio-buttons-group"
              value={toothPosition}
              onChange={handleToothPosition}
            >
              <FormControlLabel
                value={SelectPositionType.Bottom}
                control={<Radio />}
                label={t("stlPrinting.Bottom")}
              />
              <FormControlLabel
                value={SelectPositionType.LingualWall}
                control={<Radio />}
                label={t("stlPrinting.LingualWall")}
              />
              <FormControlLabel
                value={SelectPositionType.BuccalWall}
                control={<Radio />}
                label={t("stlPrinting.BuccalWall")}
              />
            </RadioGroup>
          </FormControl>
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          color: "rgba(0, 0, 0, 0.60)",
        }}
      >
        <Typography variant="body1">
          {t("stlPrinting.LabelComposer")}
        </Typography>
        <Typography variant="subtitle2">
          <Trans
            components={{
              bold: (
                <b
                  style={{
                    fontWeight: "700",
                  }}
                />
              ),
            }}
          >
            {t("stlPrinting.LabelDropdownDesc")}
          </Trans>
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              wrap: "wrap",
              pt: 2,
            }}
          >
            {caseTag &&
              [0, 1, 2, 3].map((index) => (
                <CustomDropdown
                  key={index}
                  value={String(caseTag[index] ?? CaseTagType.Empty)}
                  handleChange={(value) => handleDropdownChange(index, value)}
                  options={LABEL_COMPOSER_OPTIONS}
                />
              ))}
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              pt: 2,
              px: "40px",
              gap: "120px",
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: "600", color: "secondary" }}>
                Sample Data
              </Typography>
              {Object.entries(staticSampleData).map(([key, value]) => (
                <Box key={key} sx={{ display: "flex", mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ color: "secondary", minWidth: "120px" }}>
                    {key}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(0, 0, 0, 0.87)" }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ minWidth: "250px" }}>
              <Typography variant="body1" sx={{ fontWeight: "600", mb: 1 }}>
                Example Output
              </Typography>
              <Box
                sx={{
                  width: "fit-content",
                  height: "40px",
                  borderRadius: "4px",
                  backgroundColor: "#F5F5F5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 1.5,
                  mb: 0.5
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "700",
                    color: validationErrors.length > 0 ? "#D32F2F" : "rgba(0, 0, 0, 0.87)",
                  }}
                >
                  {exampleOutput || "JamAnderso"}
                </Typography>
              </Box>
              {validationErrors.length > 0 && (
                <Typography variant="caption" sx={{ color: "#D32F2F", fontSize: "0.75rem", mt: 0.5 }}>
                  {validationErrors.join(", ")}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
